import React, { useState, useEffect, useRef } from "react";
import { 
  FaFolder, 
  FaFolderOpen, 
  FaFile, 
  FaArrowUp, 
  FaHome, 
  FaTimes, 
  FaCopy, 
  FaCut, 
  FaTrash, 
  FaInfoCircle,
  FaFileAlt,
  FaFileCode,
  FaFileImage,
  FaFileArchive,
  FaFileVideo,
  FaFileAudio,
  FaFilePdf,
  FaChevronRight,
  FaSync
} from "react-icons/fa";
import api from "../api";
import { getApiUrl } from "../utils/apiConfig";
import "../styles/FileBrowser.css";

const baseURL = `${getApiUrl()}/api`;

const FileBrowser = ({ profileUuid, isOpen, onClose }) => {
  const [currentPath, setCurrentPath] = useState("");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [hoveredEntry, setHoveredEntry] = useState(null);
  const contextMenuRef = useRef(null);

  // Fetch directory contents
  useEffect(() => {
    if (isOpen && profileUuid) {
      fetchCurrentDirectory();
    }
  }, [isOpen, profileUuid]);

  // Handle click outside context menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
        setContextMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchCurrentDirectory = async (path = ".") => {
    if (!profileUuid) {
      setError("No profile selected");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Call the agent to list directory
      const response = await api.post(`${baseURL}/profiles/${profileUuid}/filesystem/list/`, {
        path: path
      });

      if (response.data.success) {
        setCurrentPath(response.data.path);
        setEntries(response.data.entries || []);
      } else {
        setError(response.data.error || "Failed to list directory");
      }
    } catch (err) {
      console.error("Error fetching directory:", err);
      setError("Failed to fetch directory contents");
    } finally {
      setLoading(false);
    }
  };

  const navigateToDirectory = (path) => {
    fetchCurrentDirectory(path);
  };

  const navigateUp = () => {
    if (!currentPath) return;
    
    // Get parent directory
    const pathParts = currentPath.split(/[/\\]/);
    pathParts.pop();
    const parentPath = pathParts.join("/") || "/";
    
    navigateToDirectory(parentPath);
  };

  const handleEntryClick = (entry) => {
    if (entry.type === "directory") {
      navigateToDirectory(entry.path);
    }
  };

  const handleContextMenu = (e, entry) => {
    e.preventDefault();
    setSelectedEntry(entry);
    setContextMenu({
      x: e.clientX,
      y: e.clientY
    });
  };

  const handleCopyPath = async () => {
    if (selectedEntry) {
      try {
        await navigator.clipboard.writeText(selectedEntry.path);
        setContextMenu(null);
      } catch (err) {
        console.error("Failed to copy path:", err);
      }
    }
  };

  const handleCopyCurrentPath = async () => {
    try {
      await navigator.clipboard.writeText(currentPath || "/");
    } catch (err) {
      console.error("Failed to copy current path:", err);
    }
  };

  const handleCopyFile = async () => {
    if (!selectedEntry || !profileUuid) return;

    try {
      const newName = prompt(`Copy ${selectedEntry.name} to:`, selectedEntry.path + "_copy");
      if (!newName) return;

      const response = await api.post(`${baseURL}/profiles/${profileUuid}/filesystem/copy/`, {
        source: selectedEntry.path,
        destination: newName
      });

      if (response.data.success) {
        fetchCurrentDirectory(currentPath);
        setContextMenu(null);
      } else {
        alert("Failed to copy: " + response.data.error);
      }
    } catch (err) {
      console.error("Error copying file:", err);
      alert("Failed to copy file");
    }
  };

  const handleMoveFile = async () => {
    if (!selectedEntry || !profileUuid) return;

    try {
      const newPath = prompt(`Move/Rename ${selectedEntry.name} to:`, selectedEntry.path);
      if (!newPath || newPath === selectedEntry.path) return;

      const response = await api.post(`${baseURL}/profiles/${profileUuid}/filesystem/move/`, {
        source: selectedEntry.path,
        destination: newPath
      });

      if (response.data.success) {
        fetchCurrentDirectory(currentPath);
        setContextMenu(null);
      } else {
        alert("Failed to move: " + response.data.error);
      }
    } catch (err) {
      console.error("Error moving file:", err);
      alert("Failed to move file");
    }
  };

  const handleDeleteFile = async () => {
    if (!selectedEntry || !profileUuid) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedEntry.name}?`
    );
    
    if (!confirmed) return;

    try {
      const response = await api.post(`${baseURL}/profiles/${profileUuid}/filesystem/delete/`, {
        path: selectedEntry.path
      });

      if (response.data.success) {
        fetchCurrentDirectory(currentPath);
        setContextMenu(null);
      } else {
        alert("Failed to delete: " + response.data.error);
      }
    } catch (err) {
      console.error("Error deleting file:", err);
      alert("Failed to delete file");
    }
  };

  const handleFileInfo = async () => {
    if (!selectedEntry || !profileUuid) return;

    try {
      const response = await api.post(`${baseURL}/profiles/${profileUuid}/filesystem/info/`, {
        path: selectedEntry.path
      });

      if (response.data.success) {
        const info = response.data;
        const message = `
Name: ${info.name}
Type: ${info.type}
Size: ${formatFileSize(info.size)}
Created: ${new Date(info.created).toLocaleString()}
Modified: ${new Date(info.modified).toLocaleString()}
Permissions: ${info.permissions}
        `.trim();
        
        alert(message);
        setContextMenu(null);
      } else {
        alert("Failed to get info: " + response.data.error);
      }
    } catch (err) {
      console.error("Error getting file info:", err);
      alert("Failed to get file info");
    }
  };

  const getFileIcon = (entry) => {
    if (entry.type === "directory") {
      return <FaFolder className="entry-icon folder-icon" />;
    }

    const ext = entry.extension?.toLowerCase() || "";
    
    if ([".jpg", ".jpeg", ".png", ".gif", ".bmp", ".svg"].includes(ext)) {
      return <FaFileImage className="entry-icon file-icon-image" />;
    }
    if ([".js", ".jsx", ".ts", ".tsx", ".py", ".java", ".cpp", ".c", ".h"].includes(ext)) {
      return <FaFileCode className="entry-icon file-icon-code" />;
    }
    if ([".zip", ".rar", ".7z", ".tar", ".gz"].includes(ext)) {
      return <FaFileArchive className="entry-icon file-icon-archive" />;
    }
    if ([".mp4", ".avi", ".mkv", ".mov", ".wmv"].includes(ext)) {
      return <FaFileVideo className="entry-icon file-icon-video" />;
    }
    if ([".mp3", ".wav", ".flac", ".ogg"].includes(ext)) {
      return <FaFileAudio className="entry-icon file-icon-audio" />;
    }
    if (ext === ".pdf") {
      return <FaFilePdf className="entry-icon file-icon-pdf" />;
    }
    if ([".txt", ".md", ".doc", ".docx"].includes(ext)) {
      return <FaFileAlt className="entry-icon file-icon-text" />;
    }

    return <FaFile className="entry-icon file-icon-default" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  // Get breadcrumb parts from path
  const getBreadcrumbs = () => {
    if (!currentPath) return [{ name: "/", path: "/" }];
    
    const parts = currentPath.split(/[/\\]/).filter(Boolean);
    const breadcrumbs = [{ name: "~", path: "/" }];
    
    let accPath = "";
    parts.forEach((part, index) => {
      accPath += "/" + part;
      // Only show last 2 parts plus root
      if (index >= parts.length - 2) {
        breadcrumbs.push({ name: part, path: accPath });
      } else if (index === parts.length - 3) {
        breadcrumbs.push({ name: "...", path: null });
      }
    });
    
    return breadcrumbs;
  };

  // Count directories and files
  const getStats = () => {
    const dirs = entries.filter(e => e.type === "directory").length;
    const files = entries.filter(e => e.type === "file").length;
    return { dirs, files };
  };

  if (!isOpen) return null;

  const stats = getStats();
  const breadcrumbs = getBreadcrumbs();

  return (
    <div className="file-browser-sidebar">
      {/* Header */}
      <div className="file-browser-header">
        <div className="header-title">
          <FaFolder className="header-icon" />
          <span>File Browser</span>
        </div>
        <button className="close-button" onClick={onClose} title="Close">
          <FaTimes />
        </button>
      </div>

      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-nav">
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={index}>
            {index > 0 && <FaChevronRight className="breadcrumb-separator" />}
            {crumb.path ? (
              <button 
                className={`breadcrumb-item ${index === breadcrumbs.length - 1 ? 'active' : ''}`}
                onClick={() => crumb.path && navigateToDirectory(crumb.path)}
              >
                {crumb.name}
              </button>
            ) : (
              <span className="breadcrumb-ellipsis">{crumb.name}</span>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Toolbar */}
      <div className="file-browser-toolbar">
        <div className="toolbar-left">
          <button 
            className="toolbar-btn" 
            onClick={() => navigateToDirectory("/")}
            title="Go to root"
          >
            <FaHome />
          </button>
          <button 
            className="toolbar-btn" 
            onClick={navigateUp}
            title="Go up"
            disabled={!currentPath || currentPath === "/"}
          >
            <FaArrowUp />
          </button>
          <button 
            className="toolbar-btn" 
            onClick={() => fetchCurrentDirectory(currentPath || ".")}
            title="Refresh"
          >
            <FaSync className={loading ? 'spinning' : ''} />
          </button>
        </div>
        <div className="toolbar-right">
          <button 
            className="toolbar-btn copy-path-btn" 
            onClick={handleCopyCurrentPath}
            title="Copy path"
          >
            <FaCopy />
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      {!loading && !error && entries.length > 0 && (
        <div className="stats-bar">
          <span className="stat-item">
            <FaFolder className="stat-icon folder" />
            {stats.dirs} folders
          </span>
          <span className="stat-item">
            <FaFile className="stat-icon file" />
            {stats.files} files
          </span>
        </div>
      )}

      {/* Content */}
      <div className="file-browser-content">
        {loading && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <span>Loading directory...</span>
          </div>
        )}

        {error && (
          <div className="error-state">
            <span className="error-icon">!</span>
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && entries.length === 0 && (
          <div className="empty-state">
            <FaFolderOpen className="empty-icon" />
            <span>This folder is empty</span>
          </div>
        )}

        {!loading && !error && entries.length > 0 && (
          <div className="entries-list">
            {entries.map((entry, index) => (
              <div
                key={index}
                className={`entry-item ${entry.type}`}
                onClick={() => handleEntryClick(entry)}
                onContextMenu={(e) => handleContextMenu(e, entry)}
                onMouseEnter={() => setHoveredEntry(index)}
                onMouseLeave={() => setHoveredEntry(null)}
              >
                <div className="entry-main">
                  {getFileIcon(entry)}
                  <span className="entry-name">{entry.name}</span>
                </div>
                <div className="entry-meta">
                  {entry.type === "file" && (
                    <span className="entry-size">{formatFileSize(entry.size)}</span>
                  )}
                  {entry.type === "directory" && (
                    <FaChevronRight className="entry-arrow" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="context-menu"
          style={{
            top: contextMenu.y,
            left: Math.min(contextMenu.x, window.innerWidth - 200)
          }}
        >
          <button className="context-menu-item" onClick={handleCopyPath}>
            <FaCopy /> Copy Path
          </button>
          <button className="context-menu-item" onClick={handleCopyFile}>
            <FaCopy /> Duplicate
          </button>
          <button className="context-menu-item" onClick={handleMoveFile}>
            <FaCut /> Move / Rename
          </button>
          <div className="context-menu-divider" />
          <button className="context-menu-item" onClick={handleFileInfo}>
            <FaInfoCircle /> Properties
          </button>
          <div className="context-menu-divider" />
          <button className="context-menu-item danger" onClick={handleDeleteFile}>
            <FaTrash /> Delete
          </button>
        </div>
      )}
    </div>
  );
};

export default FileBrowser;
